import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  EstimateItemModel,
  SubscriptionEstimatesModel,
} from '@app/+authenticated/+reports/shared/subscriptions/subscription.model';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { SharedPipeModule } from '@app/pipes/shared-pipe.module';
import { SubscriptionCurrencyPipe } from '@app/pipes/subscription-currency.pipe';
import { getAccountState } from '@app/reducers/account/account-service.selectors';
import { VatResponseStatus } from '@app/shared/billing/billing.model';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { featureFlag } from '@sb/shared/feature';
import { BadgeComponent } from '@sb/ui/badge';
import { SwitchComponent } from '@sb/ui/switch';
import { ContentStateComponent } from '@shared/content-state/content-state.component';
import { InvoicePeriodType, PlanType, SubscriptionModel } from '@shiftbase-com/ts-shared/models';
import { filter, Subscription, switchMap, take } from 'rxjs';

import { VatChanges } from '../forms/billing-details/billing-details-form.component';
import { OrderSummaryContentComponent } from './order-summary-content/order-summary-content.component';

@Component({
  selector: 'order-summary',
  templateUrl: './order-summary.component.html',
  imports: [
    CommonModule,
    TranslatePipe,
    SharedPipeModule,
    ContentStateComponent,
    SwitchComponent,
    ReactiveFormsModule,
    BadgeComponent,
    SubscriptionCurrencyPipe,
    OrderSummaryContentComponent,
  ],
  styles: [':host { display: block; }'],
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  subscriptionService = inject(SubscriptionService);
  private cd = inject(ChangeDetectorRef);
  private store = inject<Store<AppState>>(Store);

  selectedSubscription: SubscriptionModel;
  estimates: SubscriptionEstimatesModel;
  estimatesError = false;
  private dataSubs = new Subscription();
  features = featureFlag;
  hasIntegrationPlus: boolean;
  hasCompliancePlus: boolean;
  hasHrPlus: boolean;
  hasChat: boolean;
  countryCode: string;
  planItem: EstimateItemModel;
  vatStatus: VatResponseStatus;
  vatNumber: string;

  @Output()
  estimatesDone = new EventEmitter<boolean>();

  hasTrial: boolean;

  @Input()
  yearlyBillingToggle: FormControl<boolean>;
  @Input()
  set vatInfo(changes: VatChanges) {
    if (changes) {
      this.vatStatus = changes.status;

      if (changes.vatNumber !== this.vatNumber) {
        this.vatNumber = changes.vatNumber;
        this.getEstimates();
      }
    }
  }
  @Input()
  set newCountryCode(code: string) {
    if (code && code !== this.countryCode) {
      this.countryCode = code;
      this.getEstimates();
    }
  }
  @Input()
  skipBilling: boolean;

  @Output()
  estimatesFailed = new EventEmitter<HttpErrorResponse>();

  invoicePeriod: InvoicePeriodType;

  ngOnInit() {
    const selectedSubscription = this.subscriptionService.selectedSubscriptionValue;

    this.selectedSubscription = selectedSubscription?.subscription;
    this.hasIntegrationPlus = selectedSubscription?.integrationPlus;
    this.hasCompliancePlus = selectedSubscription?.compliance;
    this.hasHrPlus = selectedSubscription?.hrPlus;
    this.hasChat = selectedSubscription?.chat;
    this.invoicePeriod = selectedSubscription?.invoicePeriod;

    this.yearlyBillingToggle.setValue(this.invoicePeriod === InvoicePeriodType.YEAR);

    this.dataSubs.add(
      this.store.select(getAccountState).subscribe((state) => {
        this.countryCode = state.account.country;
        this.hasTrial = state.account.hasTrial;

        if (this.selectedSubscription && this.skipBilling) {
          this.getEstimates();
        }
      }),
    );

    this.dataSubs.add(
      this.yearlyBillingToggle.valueChanges.subscribe((value) => {
        this.invoicePeriod = value ? InvoicePeriodType.YEAR : InvoicePeriodType.MONTH;
        this.subscriptionService.setSelectedSubscription({
          subscription: this.selectedSubscription,
          invoicePeriod: this.invoicePeriod,
          integrationPlus: this.hasIntegrationPlus,
          compliance: this.hasCompliancePlus,
          hrPlus: this.hasHrPlus,
          chat: this.hasChat,
        });
        this.getEstimates();
        this.cd.detectChanges();
      }),
    );

    if (!selectedSubscription) {
      return;
    }

    if (this.selectedSubscription.plan.type === PlanType.FREE) {
      this.yearlyBillingToggle.disable();
    }

    this.dataSubs.add(
      this.store
        .select(getAccountState)
        .pipe(
          // Make sure account is loaded
          filter((state) => !!state?.account),
          // Only take one to initialize
          // and avoid overwriting newCountryCode
          take(1),
        )
        .subscribe((state) => {
          this.countryCode = state.account.country;
        }),
    );
  }

  private getEstimates() {
    if (!this.countryCode || this.vatStatus === VatResponseStatus.INVALID) {
      this.estimatesError = this.vatStatus === VatResponseStatus.INVALID;
      return;
    }

    // Clear estimates to trigger "loading" state
    this.estimates = null;
    this.estimatesError = false;

    this.dataSubs.add(
      this.subscriptionService
        .getSelectedSubscription()
        .pipe(
          filter((subscription) => !!subscription),
          take(1),
          switchMap((selectedSubscription) =>
            this.subscriptionService.getEstimates({
              plan: selectedSubscription.subscription.plan.type,
              integrationPlus: selectedSubscription.integrationPlus,
              compliance: selectedSubscription.compliance,
              hrPlus: selectedSubscription.hrPlus,
              chat: selectedSubscription.chat,
              country: this.countryCode,
              vatNumber: this.vatNumber,
              invoicePeriod: selectedSubscription.invoicePeriod,
            }),
          ),
        )
        .subscribe({
          next: (estimate: SubscriptionEstimatesModel) => {
            this.estimates = estimate;

            this.planItem = this.estimates.line_items?.find((item) => item.entity_type === 'plan_item_price');

            this.estimatesDone.emit(true);

            this.cd.detectChanges();
          },
          error: (error: HttpErrorResponse) => {
            this.estimatesError = true;
            this.estimatesFailed.emit(error);
            this.cd.detectChanges();
          },
        }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }
}
